import React, { useState, useMemo } from 'react';
import { Select } from 'tea-component';
import { useDeepCompareEffect } from 'ahooks';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import ShotSelection from './ShotSelect';
import SourceExplanation from '@/pages/intelligence/components/SummaryCard/components/SourceExplanation';
import MetricSelect from '../MetricSelect/index';
import { toDateTypeLy } from '@/pages/intelligence/utils/common';
import { METRIC_SUBKEY_METRICS, METRIC_SUBKEY_METRIC_TYPE, METRIC_SUBKEY_SHOT } from '@intelligence/components/Filter/FilterBase/_config';
import {
  metricsType,
  defaultMetricTypeVals,
  defaultSourceVals,
  defaultDataSourceList,
  defaultMetricTypeList,
  allMetricsOptions,
  allMetricsSourcesOption,
  ALLMETRICSSOURCESSHORTOPTION,
  ALLMETRICS_SHORT_TYPES_DAILY,
  ALLMETRICS_SHORT_TYPES_WEEKLY,
  ALLMETRICS_SHORT_TYPES_MONTHLY,
  ALLMETRICS_SHORT_TYPES_COMPARE,
  defaultMDList,
  defaultWList,
  getDefaultMetricsOptions,
  selectCutType,
  selectCutTypeWeek,
  DEFAULTSELECTSHOWRTLIST,
  filterMetricsOptionsByDataSources,
  getShortCutNameCur,
  selectCutTypeDay,
  All_METRICS_TYPE,
  selectCutTypeMonth,
  SEGMENT_DEFAULT_EXCLUDE_DATA_SOURCES,
  getMetricOptionByValue,
} from './config';
import SummaryCard from '@/pages/intelligence/components/SummaryCard';
import type {
  IAllMetricsSaveParams,
  MetricsSelectionProps,
  ConfirmParams,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/AllMetrics/types';
import {
  filterByDateType,
  getObjectByList,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/AllMetrics/utils';
import { DATABRAIN_CALIBRATION_METRICS } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/const';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import {
  PC_CONSOLE_COMPARE_SOURCE_OPTIONS,
  PC_CONSOLE_METRIC_TYPE_OPTIONS,
  PC_CONSOLE_SOURCE_OPTIONS,
  filterPCConsoleMetricTypeOptionsByMetrics,
  getPCConsoleMetricDisplayCategory,
  orderPCConsoleMetrics,
  normalizePCConsoleMetricType,
} from '@intelligence/pages/TopCharts/_config/pcConsoleMetricOrdering';

const isDatabrainCalibrationMetricSelection = (list: string[] = []) => {
  if (!Array.isArray(list)) return false;
  if (list.length !== DATABRAIN_CALIBRATION_METRICS.length) return false;
  const metricsSet = new Set(list);
  return DATABRAIN_CALIBRATION_METRICS.every((metric) => metricsSet.has(metric));
};

const MetricsSelection = (props: MetricsSelectionProps) => {
  const {
    type,
    onChange: onChangeHandle,
    loading,
    metricTypeVals,
    sourceVals,
    metricsVals,
    selectedMetricsValue = 'metrics',
    shotSelectValue = 'all',
    selectCutVal = 'hot',
    isShowSourceTips = true,
    isSelectShortCut = false,
    /**
     * 禁用的数据源键列表
     */
    disabledDataSources,
    excludeMetricValues,
    overlayClassName,
    hasDatabrainData,
    entityType,
  } = props;

  const isPCConsoleCombine = entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE;

  // console.log('MetricsSelection~~~~~~~~~~~~~~~', props);
  const [defaultMetrics, setDefaultMetrics] = useState<string[]>(metricsVals || []);
  const [defaultDateType, setDefaultDateType] = useState<string>(toDateTypeLy[type]);
  const [metricsValueList, setMetricsValueList] = useState<string[]>(metricsVals || []);
  const [selectedMetrics, setSelectedMetrics] = useState<string>(selectedMetricsValue);
  const [shotSelect, setShotSelect] = useState<string>(shotSelectValue);
  const [selectCutValue, setSelectCutValue] = useState<string>(selectCutVal);
  const [openSource, setOpenSource] = useState(false);
  const [dateTypeValue, setDateTypeValue] = useState(toDateTypeLy[type]);
  const { formatMessage, locale } = useIntlExtend();

  const shotList = useMemo(() => {
    if (selectedMetrics === 'metrics') return defaultMetricTypeList;
    return defaultDataSourceList.map((item) => {
      return { ...item, disabled: disabledDataSources?.includes(item?.value) };
    });
  }, [selectedMetrics, disabledDataSources, defaultMetricTypeList]);

  const keyEnums = useMemo(() => {
    if (isSelectShortCut) return 'selectShortType';
    return selectedMetrics === 'metrics' ? 'metricsType' : 'dataSource';
  }, [selectedMetrics, isSelectShortCut]);

  const typeCur = useMemo(() => {
    return toDateTypeLy[dateTypeValue];
  }, [dateTypeValue]);

  const filterMetricsByDateType = useMemo(() => {
    const list = allMetricsOptions.filter((element: any) =>
      element.dateEnum.includes(typeCur),
    );
    const orderedList = isPCConsoleCombine ? orderPCConsoleMetrics(list) : list;
    if (!excludeMetricValues?.length) return orderedList;
    const excludeSet = new Set(excludeMetricValues);
    return orderedList.filter(item => !excludeSet.has(item.value));
  }, [typeCur, excludeMetricValues, isPCConsoleCombine]);

  const shortcutExcludedDataSources = useMemo(() => {
    return Array.from(
      new Set([
        ...(isSelectShortCut ? [] : SEGMENT_DEFAULT_EXCLUDE_DATA_SOURCES),
        ...(disabledDataSources || []),
      ]),
    );
  }, [disabledDataSources, isSelectShortCut]);

  const availableMetricsByDateType = useMemo(() => {
    return filterMetricsOptionsByDataSources(filterMetricsByDateType, shortcutExcludedDataSources);
  }, [filterMetricsByDateType, shortcutExcludedDataSources]);

  const pcConsoleMetricTypeOptions = useMemo(() => {
    if (!isPCConsoleCombine) return undefined;
    // 只隐藏当前粒度完全没有指标的分类；指标本身的 disabled 仍由原有数据源/日期规则负责。
    return filterPCConsoleMetricTypeOptionsByMetrics(
      PC_CONSOLE_METRIC_TYPE_OPTIONS,
      filterMetricsByDateType,
    );
  }, [filterMetricsByDateType, isPCConsoleCombine]);

  const sourcesOptionsCur = useMemo(() => {
    if (isPCConsoleCombine) {
      return isSelectShortCut
        ? PC_CONSOLE_COMPARE_SOURCE_OPTIONS
        : PC_CONSOLE_SOURCE_OPTIONS;
    }
    return isSelectShortCut ? ALLMETRICSSOURCESSHORTOPTION : allMetricsSourcesOption;
  }, [allMetricsSourcesOption, isSelectShortCut, ALLMETRICSSOURCESSHORTOPTION, isPCConsoleCombine]);

  const allMetricsTypesCur = useMemo(() => {
    if (isPCConsoleCombine) {
      return pcConsoleMetricTypeOptions || [];
    }
    if (!isSelectShortCut) {
      switch (typeCur) {
        case 'weekly':
          return ALLMETRICS_SHORT_TYPES_WEEKLY;
        case 'daily':
          return ALLMETRICS_SHORT_TYPES_DAILY;
        case 'monthly':
        default:
          return ALLMETRICS_SHORT_TYPES_MONTHLY;
      }
    } else {
      return ALLMETRICS_SHORT_TYPES_COMPARE;
    }
  }, [
    isSelectShortCut,
    typeCur,
    ALLMETRICS_SHORT_TYPES_DAILY,
    ALLMETRICS_SHORT_TYPES_WEEKLY,
    ALLMETRICS_SHORT_TYPES_MONTHLY,
    ALLMETRICS_SHORT_TYPES_COMPARE,
    isPCConsoleCombine,
    pcConsoleMetricTypeOptions,
  ]);

  const allMetricTypeOptionsCur = isPCConsoleCombine
    ? pcConsoleMetricTypeOptions || []
    : All_METRICS_TYPE;

  const defaultListCur = useMemo(() => {
    if (isSelectShortCut) return DEFAULTSELECTSHOWRTLIST;
    return typeCur === 'weekly' ? defaultWList : defaultMDList;
  }, [typeCur, isSelectShortCut]);

  // 获取已选中的options
  const getDataByValue = (list: any) => {
    try {
      return allMetricsOptions?.filter((element: any) => {
        return list?.includes(element.value);
      });
    } catch (exception) {
      return getDefaultMetricsOptions();
    }
  };

  // 处理指标类型变化
  const handleMetricsTypeChange = (value: string) => {
    setSelectedMetrics(value);
    setShotSelect('all');
    updateFilterData(metricsValueList, 'all', METRIC_SUBKEY_METRIC_TYPE);
  };

  // 判断是否是一个指标内

  // 快捷选择点击事件-btn
  const handleShotBtnChange = (val: string) => {
    setShotSelect(val);
    let defaultList: string[] = [];
    if (val === 'all') {
      defaultList = defaultListCur;
    } else {
      availableMetricsByDateType.forEach((ele) => {
        if (ele[keyEnums] === val) {
          defaultList.push(ele.value);
        }
      });
    }

    if (defaultList.length < 1) {
      setShotSelect('all');
      updateFilterData(defaultListCur, 'all', METRIC_SUBKEY_SHOT);
      return;
    }

    updateFilterData(defaultList, val, METRIC_SUBKEY_SHOT);
  };

  // 快捷选择点击事件-select
  const handleShotSelectChange = (val: string) => {
    let defaultList: string[] = [];
    if (val === 'all') {
      defaultList = defaultListCur;
    } else {
      availableMetricsByDateType.forEach((ele) => {
        if (ele[keyEnums]?.includes(val)) {
          defaultList.push(ele.value);
        }
      });
    }
    if (Array.isArray(defaultList) && defaultList?.length < 1) {
      defaultList = defaultListCur;
    }
    updateFilterData(defaultList, val, METRIC_SUBKEY_SHOT);
  };

  // 数据重新拼装
  const updateFilterData = (defaultList: any, shotType: string, subKey: string) => {
    const opt = getDataByValue(defaultList);
    const { filterOption, filterList } = filterByDateType(opt, typeCur, isSelectShortCut);
    const metricsObj = getObjectByList(filterOption);
    const userSaveParams: IAllMetricsSaveParams = {
      sources: sourceVals,
      types: metricTypeVals,
      metrics: filterList,
      source_metrics: metricsObj?.source_metrics,
      selectedMetricsValue: selectedMetrics,
      shotSelectValue: isSelectShortCut ? shotSelect : shotType,
      selectCutVal: isSelectShortCut ? shotType : selectCutValue,
      type: dateTypeValue,
    };

    onChangeHandle({
      metricsOptions: filterOption,
      metricsOptionsByConfigOrder: getMetricOptionByValue(filterList, true),
      ...userSaveParams,
      subKey: subKey || METRIC_SUBKEY_METRICS,
    });
    setMetricsValueList(filterList);
    setDefaultMetrics(filterList);
  };

  // 下拉筛选器点击confirm事件的回调
  const handleConfirm = (params: ConfirmParams) => {
    /** *** 获取需要的参数，并保存到接口，以便下次登录使用 */
    // console.log('handleConfirm', params);
    // console.log('handleConfirm dateTypeValue', dateTypeValue);
    // console.log('handleConfirm typeCur', typeCur);
    const { metricsObj, metricsOptions = [], metricsValue, metricTypeValue, sourcesValue } = params;
    const isDatabrainSelection = isDatabrainCalibrationMetricSelection(metricsValue);
    const nextSelectedMetricsValue = isDatabrainSelection ? 'dataSource' : 'metrics';
    const nextShotSelectValue = isDatabrainSelection ? 'databrain' : 'all';

    const userSaveParams: IAllMetricsSaveParams = {
      sources: sourcesValue,
      types: metricTypeValue,
      metrics: metricsValue,
      source_metrics: metricsObj?.source_metrics,
      selectedMetricsValue: nextSelectedMetricsValue,
      shotSelectValue: nextShotSelectValue,
      selectCutVal: 'all',
      type: dateTypeValue,
    };

    onChangeHandle({
      metricsOptions,
      metricsOptionsByConfigOrder: getMetricOptionByValue(metricsValue, true),
      ...userSaveParams,
      subKey: METRIC_SUBKEY_METRICS,
    });
    /** *** 获取需要的参数，并保存到接口，以便下次登录使用 */
    setDefaultMetrics(metricsValue);
    setMetricsValueList(metricsValue);
    checkIsOnlyOneMetric(metricsValue);
    setSelectCutValue('all');

    if (isDatabrainSelection) {
      setSelectedMetrics('dataSource');
      setShotSelect('databrain');
    } else {
      setSelectedMetrics('metrics');
      setShotSelect('all');
    }
    // return metricTarget;
  };

  // 判断是否是一个指标内
  const checkIsOnlyOneMetric = (metricsValue: string[]) => {
    if (shotSelect === 'all' || !metricsValue?.length) return;
    const defaultList: string[] = [];
    availableMetricsByDateType.forEach((ele) => {
      if (ele[keyEnums] === shotSelect) {
        defaultList.push(ele.value);
      }
    });
    if (!defaultList?.length) return;
    if (metricsValue?.length > defaultList?.length) {
      setShotSelect('all');
    } else if (metricsValue?.length === defaultList?.length) {
      const isTheSame = metricsValue.every((ele: string) => defaultList.includes(ele));
      if (!isTheSame) setShotSelect('all');
    }
  };

  const handleReset = () => {
    const list = defaultListCur;
    setShotSelect('all');
    setMetricsValueList(list);
  };

  const handleClickSourceTips = () => {
    setOpenSource(!openSource);
  };

  useDeepCompareEffect(() => {
    setDateTypeValue(toDateTypeLy[type]);
    setDefaultDateType(toDateTypeLy[type]);
  }, [type]);
  useDeepCompareEffect(() => {
    setMetricsValueList(metricsVals || []);
    setDefaultMetrics(metricsVals || []);
  }, [metricsVals]);
  useDeepCompareEffect(() => {
    setShotSelect(shotSelectValue);
  }, [shotSelectValue]);
  useDeepCompareEffect(() => {
    setSelectedMetrics(selectedMetricsValue);
  }, [selectedMetricsValue]);
  useDeepCompareEffect(() => {
    setSelectCutValue(selectCutVal);
  }, [selectCutVal]);

  const selectCutTypeCur = useMemo(() => {
    // console.debug('typeCur', { typeCur, selectCutTypeMonth });

    switch (typeCur) {
      case 'weekly':
        return selectCutTypeWeek;
      case 'daily':
        return selectCutTypeDay;
      case 'monthly':
        return selectCutTypeMonth;
      default:
        return selectCutType;
    }
  }, [typeCur]);

  const selectCutValueCur = useMemo(() => {
    if (loading) return '';
    return getShortCutNameCur(metricsVals, typeCur, selectCutTypeCur);
  }, [loading, metricsVals, typeCur, selectCutTypeCur]);

  const shotName = useMemo(() => {
    if (isSelectShortCut)
      return selectCutTypeCur.find((element) => element.value === selectCutValueCur)?.label;
    return shotList.find((element) => element.value === shotSelect)?.text;
  }, [isSelectShortCut, shotSelect, shotList, selectCutTypeCur, selectCutValueCur]);

  const selectedMetricsCur = useMemo(() => {
    if (loading) return '';
    return selectedMetrics;
  }, [selectedMetrics, loading]);

  const selectCutTextCur = useMemo(() => {
    if (loading) return '';
    return formatMessage({
      id: selectCutTypeCur?.find((item) => item.value === selectCutValueCur)?.label || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCutValueCur, loading, selectCutTypeCur, locale]);

  // console.debug('selectCutTextCur', { selectCutTextCur })

  return (
    <>
      <div className="dbi_metric_select-box">
        {isSelectShortCut ? (
          <Select
            className="dbi-metric-selectCut-header"
            appearance="button"
            button={
              <div className="dbi-metric-select">
                <div className="dbi-metric-select-lable">
                  {formatMessage({ id: 'dbi_commpare_metric_metrics_set' })}
                </div>
                <div className="dbi-metric-select-text">
                  <div className="dbi-metric-select-value">{selectCutTextCur}</div>
                </div>
              </div>
            }
            options={selectCutTypeCur.map((metrics) => ({
              ...metrics,
              text: formatMessage({ id: metrics?.label }),
            }))}
            onChange={(value) => {
              setSelectCutValue(value);
              handleShotSelectChange(value);
            }}
            value={selectCutValueCur}
            matchButtonWidth={true}
            listHeight={276}
          />
        ) : (
          <>
            <Select
              appearance="button"
              options={metricsType.map((metrics) => ({
                ...metrics,
                text: formatMessage({ id: metrics?.label }),
              }))}
              onChange={(value) => {
                handleMetricsTypeChange(value);
              }}
              value={selectedMetricsCur}
            />
            <div className="dbi_metric_divider"></div>
            <ShotSelection
              shotSelect={shotSelect}
              selectedMetrics={selectedMetrics}
              shotList={shotList}
              loading={loading}
              onChange={handleShotBtnChange}
              type={typeCur}
            />
          </>
        )}
        <div className="dbi_metric_divider"></div>
        <MetricSelect
          shotName={shotName}
          type={typeCur}
          // loading={metricsLoading}
          loading={loading}
          metricTypeOptions={allMetricsTypesCur}
          metricTypeAllOptions={allMetricTypeOptionsCur}
          sourcesOptions={sourcesOptionsCur}
          metricsOptions={filterMetricsByDateType}
          metricTypeVals={isPCConsoleCombine ? normalizePCConsoleMetricType(metricTypeVals) : metricTypeVals}
          sourceVals={sourceVals}
          oldMetricsVals={defaultMetrics}
          metricsValueList={metricsValueList}
          setMetricsValueList={setMetricsValueList}
          // reset默认值
          defaultMetricTypeVals={defaultMetricTypeVals}
          defaultSourceVals={defaultSourceVals}
          defaultMetricsVals={defaultListCur}
          hendalConfirm={(params) => handleConfirm(params)}
          handleReset={handleReset}
          dateTypeValue={dateTypeValue}
          setDateTypeValue={setDateTypeValue}
          defaultDateType={defaultDateType}
          setShotSelect={setShotSelect}
          getDataByValue={getDataByValue}
          setSelectCutValue={setSelectCutValue}
          overlayClassName={overlayClassName}
          hasDatabrainData={hasDatabrainData}
          resolveMetricType={isPCConsoleCombine ? getPCConsoleMetricDisplayCategory : undefined}
          disableSourcesWithoutMetrics={isPCConsoleCombine}
        />
        {isShowSourceTips && (
          <div className="dbi_pcconsole_source_explanation-div">
            <SourceExplanation
              textValue={'dbi_pcconsole_source_explanation'}
              handleClickSourceState={handleClickSourceTips}
              openSource={openSource}
            />
          </div>
        )}
      </div>
      {isShowSourceTips && openSource && (
        <SummaryCard isSourceTips={true} openSource={openSource} />
      )}
    </>
  );
};
export default MetricsSelection;
